import { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit';

import {
  FaSpinner,
  FaTrashAlt,
  FaTable,
  FaCaretUp,
  FaCaretDown,
  FaCaretRight,
} from "react-icons/fa";
import { API } from "aws-amplify";
import moment from "moment";
import { concat, without, sort, prop, ascend } from "ramda";

import useInterval from "./hooks/useInterval";
import { useHistory } from 'react-router-dom';

import CreateComparisonModal from "./CreateComparisonModal";

const { SearchBar } = Search;

const getSortFromUrl = () => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.hash.split('?')[1]);
  const sortField = params.get('sort');
  const sortOrder = params.get('order');

  if (!sortField || !sortOrder) {
    return [{ // default sort if not in URL
      dataField: "date",
      order: "desc",
    }];
  }

  return [{
    dataField: sortField,
    order: sortOrder
  }];
};

const updateUrl = (field, order) => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.hash.split('?')[1]);

  params.set('sort', field);
  params.set('order', order);

  url.hash = url.hash.split('?')[0] + '?' + params.toString();
  window.history.replaceState({}, '', url.toString());
};

const SampleTable = ({ keyField, data, columns, defaultSorted, selectRow, selected, handleDelete, handleCompare }) =>
  <ToolkitProvider
    keyField={keyField}
    data={data}
    columns={columns}
    search
  >
    {
      props => (
        <div>
          <SearchBar { ...props.searchProps } />
            <span className="float-right">
            {
              selected.length > 1 && (
                  <Button
                    variant="primary"
                    disabled={selected.length <= 1}
                    onClick={handleCompare}
                  >
                    <FaTable /> Compare selected {selected?.length}
                  </Button>
                )
            }
            {" "}
            {
              selected.length > 0 && (
                <Button
                  variant="secondary"
                  disabled={selected.length === 0}
                  onClick={handleDelete}
                >
                  <FaTrashAlt /> Delete selected {selected?.length}
                </Button>
              )
            }
            </span>
          <BootstrapTable
            bootstrap4 striped bordered={false}
            defaultSorted={defaultSorted} selectRow={selectRow}
            { ...props.baseProps } />
        </div>
      )
    }
  </ToolkitProvider>;

const Samples = () => {
  const [samples, setSamples] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showCreateComparison, setShowCreateComparison] = useState(false);
  const initialSort = getSortFromUrl();
  const history = useHistory();
  const loadSamples = async () => {
    const newSamples = await API.get("mgp", "/samples");
    setSamples(sort(ascend(prop("date")), newSamples));
  };
  const handleDelete = async (event) => {
    event.preventDefault();
    setShowConfirmDelete(true);
  };
  const createComparison = async (name) => {
    const newComparison = await API.post("mgp", "/comparisons", {
      body: {
        name,
        metadata: selected.map((sampleId) => ({
          Sample_ID: sampleId,
          Sample: sampleId,
          Group: sampleId
        }))
      }
    });
    history.push(`/comparisons/${newComparison.id}`);
  };
  const handleCompare = async (event) => {
    event.preventDefault();
    setShowCreateComparison(true);
  };
  const handleComparisonCreate = async (name) => {
    setShowCreateComparison(false);
    await createComparison(name);
  };
  const handleComparisonCancel = () => {
    setShowCreateComparison(false);
  };
  const handleConfirmCancel = () => {
    setShowConfirmDelete(false);
  };
  const handleConfirm = async (event) => {
    event.preventDefault();
    try {
      for (const sampleName of selected) {
        await API.del("mgp", `/samples/${sampleName}`);
      }
      setSelected([]);
      await loadSamples();
    } catch (err) {
      alert(err.message);
    }
    setShowConfirmDelete(false);
  };
  useEffect(() => {
    (async () => {
      try {
        await loadSamples();
      } catch (err) {
        alert(err);
      }
    })();
  }, []);
  const sortCaret = (order) =>
    order === "asc" ? (
      <FaCaretUp />
    ) : order === "desc" ? (
      <FaCaretDown />
    ) : (
      <FaCaretRight style={{ visibility: "hidden" }} />
    );
  useInterval(() => {
    // refresh sample list if there are samples in 'Processing' state
    (async () => {
      try {
        if (
          samples &&
          samples.find((sample) => sample.status === "Processing")
        ) {
          await loadSamples();
        }
      } catch (err) {
        alert(err);
      }
    })();
  }, 10000);
  return (
    <>
      <Modal show={showConfirmDelete} backdrop="static">
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Delete selected {selected.length}{" "}
            {selected.length === 1 ? "sample" : "samples"}?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleConfirmCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      <CreateComparisonModal
        show={showCreateComparison}
        backdrop="static"
        onCreate={handleComparisonCreate}
        onCancel={handleComparisonCancel}
      />
      <h3>Samples</h3>
      <p>
        Start a new analysis by <a href="#/upload">uploading</a> FASTQ files.
      </p>
      <SampleTable
        keyField="name"
        data={samples}
        columns={[
          {
            dataField: "name",
            text: "Sample",
            sort: true,
            onSort: (field, order) => updateUrl(field, order),
            sortCaret,
            formatter: (name, sample) =>
              sample.status === "Processing" ? (
                name
              ) : (
                <a href={`#/samples/${name}`}>{name}</a>
              ),
          },
          {
            dataField: "project",
            text: "Project",
            sort: true,
            sortCaret,
            onSort: (field, order) => updateUrl(field, order),
          },
          {
            dataField: "files",
            text: "Files",
            sort: true,
            sortCaret,
            onSort: (field, order) => updateUrl(field, order),
            formatter: (files) =>
              files && files.length > 0 && files.map((file) => <div key={file}>{file}</div>),
          },
          {
            dataField: "date",
            text: "Date",
            sort: true,
            sortCaret,
            onSort: (field, order) => updateUrl(field, order),
            formatter: (date) =>
              date && moment(date).format("YYYY-MM-DD HH:mm"),
            filterValue: (date) =>
              date && moment(date).format("YYYY-MM-DD HH:mm"),
          },
          {
            dataField: "status",
            text: "Status",
            sort: true,
            sortCaret,
            onSort: (field, order) => updateUrl(field, order),
            formatter: (status) => (
              <>
                {status}{" "}
                {status === "Processing" && <FaSpinner className="icon-spin" />}
              </>
            ),
          },
        ]}
        defaultSorted={initialSort}
        selectRow={{
          mode: "checkbox",
          hideSelectAll: false,
          selected: selected,
          onSelect: (row, isSelect) => {
            const sampleNames = [row.name];
            setSelected(isSelect ? concat(sampleNames) : without(sampleNames));
          },
          onSelectAll: (isSelect, rows) => {
            const sampleNames = rows.map(prop("name"));
            setSelected(isSelect ? concat(sampleNames) : without(sampleNames));
          }
        }}
        selected={selected}
        handleDelete={handleDelete}
        handleCompare={handleCompare}
      />
    </>
  );
};

export default Samples;
